import type { Currency } from "@loot-os/api-contract";

import { type DraftRow, percentToPpm, rowItemId, rowValueCents } from "./dropTableDraft";

/**
 * The drop table's sort and filter. Both are a view: the draft keeps its rows in the order they
 * were added, so sorting never changes what a publish sends or which row an undo restores.
 */

export const SORT_KEYS = ["name", "marketPrice", "value", "weight"] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDirection = "asc" | "desc";
export interface DropTableSort {
  key: SortKey;
  direction: SortDirection;
}

/** A header click: ascending, then descending, then back to the table's own order. */
export function nextSort(current: DropTableSort | null, key: SortKey): DropTableSort | null {
  if (current?.key !== key) return { key, direction: "asc" };
  return current.direction === "asc" ? { key, direction: "desc" } : null;
}

export const SOURCE_FILTERS = ["all", "CATALOG", "MARKETPLACE"] as const;
export type SourceFilter = (typeof SOURCE_FILTERS)[number];

export interface DropTableView {
  sort: DropTableSort | null;
  source: SourceFilter;
  /** Case-insensitive, on the row's name or its id. */
  search: string;
}

export const DEFAULT_VIEW: DropTableView = { sort: null, source: "all", search: "" };

function sortValue(row: DraftRow, key: SortKey, currency: Currency): number | string | null {
  if (key === "name") return row.name.toLowerCase();
  if (key === "marketPrice") return row.marketPriceCents;
  if (key === "value") return rowValueCents(row, currency);
  return percentToPpm(row.weight);
}

/**
 * The rows to render, filtered and sorted. A row with no market price sorts last either way,
 * so "highest market price first" never opens on a column of dashes. Ties keep table order.
 */
export function viewRows(rows: DraftRow[], view: DropTableView, currency: Currency): DraftRow[] {
  const term = view.search.trim().toLowerCase();
  const filtered = rows.filter(
    (row) =>
      (view.source === "all" || row.itemSource === view.source) &&
      (term === "" ||
        row.name.toLowerCase().includes(term) ||
        rowItemId(row).toLowerCase().includes(term)),
  );
  const { sort } = view;
  if (!sort) return filtered;
  const sign = sort.direction === "asc" ? 1 : -1;
  return filtered
    .map((row, index) => ({ row, index, value: sortValue(row, sort.key, currency) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) {
        if (a.value === b.value) return a.index - b.index;
        return a.value === null ? 1 : -1;
      }
      const cmp =
        typeof a.value === "string" && typeof b.value === "string"
          ? a.value.localeCompare(b.value)
          : (a.value as number) - (b.value as number);
      return cmp === 0 ? a.index - b.index : sign * cmp;
    })
    .map(({ row }) => row);
}
